/* eslint-disable react/jsx-props-no-spreading */
import React, { MouseEvent, PropsWithChildren } from 'react';
import Stepper, { StepperProps } from '@material-ui/core/Stepper';
import StepConnector from '@material-ui/core/StepConnector';
import MuiStep, { StepProps as MuiStepProps } from '@material-ui/core/Step';
import StepLabel, { StepLabelProps } from '@material-ui/core/StepLabel';
import LinearProgress from '@material-ui/core/LinearProgress';

import type { Errors } from 'state/shared';
import LoadingButton from 'components/LoadingButton';

import useStyles, { useConnectorStyles } from './Stepper.style';

export type StepComponentProps = {
  updateValidation?: (isValid: boolean) => void;
  pending?: boolean;
  onNext?: (values: unknown) => void;
  formId?: string;
  errors?: Errors;
};

export type Step<P extends StepComponentProps> = {
  index: number;
  label: string;
  formId?: P['formId'];
  onNext?: (values: unknown) => void;
  stepProps?: MuiStepProps | ((activeStep: number) => MuiStepProps);
  stepLabelProps?: StepLabelProps;
  hidden?: boolean;
  component:
    | React.ComponentType<P>
    | React.ComponentType<Record<string, unknown>>;
};

export type Steps<P extends StepComponentProps> = Step<P>[];

export type StepsContainer<P extends StepComponentProps> = {
  steps: Steps<P>;
  // the actual length (including hidden steps)
  length: number;
  /* length used for display purposes (i.e.
     progress bars) - should exclude hidden steps */
  displayLength: number;
};

type HorizontalLinearStepperProps<P extends StepComponentProps> = Omit<
  StepperProps,
  'children'
> & {
  stepsContainer: StepsContainer<P>;
  currentStep?: number; // step is determined by parent (controlled component) instead of by this component's internal state
  skipToStep?: number;
  onNext?: () => boolean | Promise<boolean>;
  nextDisabled: boolean;
  pending: boolean;
};

const HorizontalLinearStepper = <P extends StepComponentProps>(
  props: PropsWithChildren<HorizontalLinearStepperProps<P>>
): React.ReactElement<P> | null => {
  // TODO: clean this up
  const {
    stepsContainer: { steps, displayLength },
    currentStep,
    skipToStep,
    onNext,
    nextDisabled,
    pending,
    connector,
  } = props;
  const {
    flexContainer,
    gridContainer,
    stepper,
    stepIconRoot,
    buttonContainer,
    button,
    linearProgressContainer,
    linearProgressBar,
    hiddenStep,
  } = useStyles();
  const { stepConnector } = useConnectorStyles(
    steps as Steps<StepComponentProps>
  );
  const [internalActiveStep, setInternalActiveStep] = React.useState(0);
  let activeStep: number;
  let setActiveStep: typeof setInternalActiveStep;
  if (currentStep || currentStep === 0) {
    activeStep = currentStep;
    setActiveStep = () => null; // set current in redux
  } else {
    activeStep = internalActiveStep;
    setActiveStep = setInternalActiveStep;
  }
  if (skipToStep) {
    setActiveStep(skipToStep);
  }

  /* only used if this is not a controlled component (currentStep not 
     being set by parent) */
  const handleNext = async (_event: MouseEvent) => {
    const proceed = onNext ? await onNext() : true;
    if (proceed && !currentStep && currentStep !== 0) {
      // update internal state (current step isn't being set by parent)
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  return (
    <div className={flexContainer}>
      <div className={gridContainer}>
        <Stepper
          activeStep={activeStep}
          connector={connector || <StepConnector className={stepConnector} />}
          className={stepper}
        >
          {steps.map((step, _index) => {
            const { label, hidden } = step;
            const stepProps =
              step.stepProps instanceof Function
                ? step.stepProps(activeStep)
                : step.stepProps || {};
            const labelProps = {
              ...step.stepLabelProps,
              StepIconProps: {
                classes: { root: stepIconRoot },
              },
              ...step.stepLabelProps?.StepIconProps,
            };

            return (
              <MuiStep
                key={label}
                className={hidden ? hiddenStep : undefined}
                {...stepProps}
              >
                <StepLabel {...labelProps}>{label}</StepLabel>
              </MuiStep>
            );
          })}
        </Stepper>
        {(onNext || steps[activeStep]?.onNext) && (
          <div className={buttonContainer}>
            <LoadingButton
              type="submit"
              form={steps[activeStep].formId}
              variant="contained"
              color="primary"
              onClick={handleNext}
              className={button}
              disabled={nextDisabled}
              pending={pending}
            >
              {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
            </LoadingButton>
          </div>
        )}
        <LinearProgress
          variant="determinate"
          value={((activeStep + 1) / (displayLength + 2)) * 100}
          classes={{ root: linearProgressContainer, bar: linearProgressBar }}
        />
      </div>
    </div>
  );
};

export default HorizontalLinearStepper;
